import { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useMutation, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { Button } from "../../shared";
import {
  confirmEmailChange,
  requestEmailChange,
} from "../../../services/personal";

interface Props {
  open: boolean;
  onClose: () => void;
  currentEmail: string | null;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Bottom/right-drawer смены email в два шага:
 *   1. Юзер вводит новый email → бэк отправляет OTP на него.
 *   2. Юзер вводит 6-значный код → email обновляется.
 *
 * Оба шага — внутри одного drawer'а, переключаются локальным state'ом.
 * При успешном подтверждении инвалидируем `user-info` и закрываемся.
 */
export function EmailChangeDrawer({ open, onClose, currentEmail }: Props) {
  const { t } = useTranslation(["profile", "errors"]);
  const qc = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [maskedEmail, setMaskedEmail] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (!open) {
      setStep("email");
      setEmail("");
      setCode("");
      setMaskedEmail(null);
      setCooldown(0);
    }
  }, [open]);

  // Локальный обратный отсчёт cooldown'а «отправить код ещё раз».
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const requestMut = useMutation({
    mutationFn: (newEmail: string) => requestEmailChange(newEmail),
    onSuccess(data) {
      setMaskedEmail(data.masked_email);
      setStep("code");
      setCooldown(30);
    },
    onError(err) {
      enqueueSnackbar(pickMessage(err), { variant: "error" });
    },
  });

  const confirmMut = useMutation({
    mutationFn: (payload: { newEmail: string; code: string }) =>
      confirmEmailChange(payload.newEmail, payload.code),
    onSuccess() {
      enqueueSnackbar(
        t("profile:personal_data.email_changed", {
          defaultValue: "E-mail обновлён",
        }),
        { variant: "success" },
      );
      qc.invalidateQueries(["user-info"]);
      onClose();
    },
    onError(err) {
      enqueueSnackbar(pickMessage(err), { variant: "error" });
    },
  });

  const emailNormalized = email.trim().toLowerCase();
  const isEmailValid = useMemo(
    () => EMAIL_RE.test(emailNormalized) && emailNormalized !== currentEmail,
    [emailNormalized, currentEmail],
  );
  const isCodeValid = /^\d{6}$/.test(code);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: 100015 }}
      slotProps={{
        backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0.55)" } },
      }}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: "100%",
          bgcolor: "background.default",
          backgroundImage: "none",
          display: "flex",
          flexDirection: "column",
          padding:
            "calc(env(safe-area-inset-top, 0px) + 16px) 16px calc(env(safe-area-inset-bottom, 0px) + 20px)",
          boxSizing: "border-box",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "20px",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{ color: "text.primary" }}
          aria-label="close"
        >
          <Icon icon="solar:alt-arrow-left-linear" width={22} height={22} />
        </IconButton>
        <Typography
          sx={{
            fontSize: "20px",
            fontWeight: 600,
            color: "text.primary",
            lineHeight: 1.2,
          }}
        >
          {t("profile:personal_data.change_email", {
            defaultValue: "Смена e-mail",
          })}
        </Typography>
      </Box>

      {step === "email" ? (
        <>
          <Typography
            sx={{
              fontSize: "14px",
              color: "text.secondary",
              marginBottom: "16px",
              lineHeight: 1.5,
            }}
          >
            {t("profile:personal_data.change_email_hint", {
              defaultValue:
                "Введите новый e-mail. Мы отправим на него 6-значный код для подтверждения.",
            })}
          </Typography>

          <TextField
            autoFocus
            fullWidth
            type="email"
            placeholder="new@mail.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            InputProps={{
              sx: {
                borderRadius: "14px",
                bgcolor: "secondary.main",
              },
            }}
            inputProps={{ inputMode: "email", autoComplete: "email" }}
          />

          <Button
            onClick={() => requestMut.mutate(emailNormalized)}
            disabled={!isEmailValid || requestMut.isLoading}
            sx={{ marginTop: "20px" }}
          >
            {requestMut.isLoading ? (
              <CircularProgress size={22} sx={{ color: "#fff" }} />
            ) : (
              t("profile:personal_data.send_code", {
                defaultValue: "Отправить код",
              })
            )}
          </Button>
        </>
      ) : (
        <>
          <Typography
            sx={{
              fontSize: "14px",
              color: "text.secondary",
              marginBottom: "16px",
              lineHeight: 1.5,
            }}
          >
            {t("profile:personal_data.check_email", {
              defaultValue: "Мы отправили код на",
            })}{" "}
            <Box component="span" sx={{ color: "text.primary", fontWeight: 500 }}>
              {maskedEmail}
            </Box>
          </Typography>

          <TextField
            autoFocus
            fullWidth
            placeholder="000000"
            value={code}
            onChange={(e) =>
              setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
            }
            InputProps={{
              sx: {
                borderRadius: "14px",
                bgcolor: "secondary.main",
                fontSize: "22px",
                letterSpacing: "8px",
                textAlign: "center",
                "& input": { textAlign: "center" },
              },
            }}
            inputProps={{ inputMode: "numeric", maxLength: 6 }}
          />

          <Button
            onClick={() =>
              confirmMut.mutate({ newEmail: emailNormalized, code })
            }
            disabled={!isCodeValid || confirmMut.isLoading}
            sx={{ marginTop: "20px" }}
          >
            {confirmMut.isLoading ? (
              <CircularProgress size={22} sx={{ color: "#fff" }} />
            ) : (
              t("profile:personal_data.confirm", { defaultValue: "Подтвердить" })
            )}
          </Button>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: "16px",
              marginTop: "18px",
            }}
          >
            {cooldown > 0 ? (
              <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
                {t("profile:personal_data.resend_in", {
                  defaultValue: "Ещё раз через",
                })}{" "}
                {cooldown} {t("profile:personal_data.seconds_short", { defaultValue: "с" })}
              </Typography>
            ) : (
              <Typography
                onClick={() => requestMut.mutate(emailNormalized)}
                sx={{
                  fontSize: "13px",
                  color: "primary.main",
                  cursor: "pointer",
                  fontWeight: 500,
                }}
              >
                {t("profile:personal_data.resend", {
                  defaultValue: "Отправить код ещё раз",
                })}
              </Typography>
            )}
            <Typography
              onClick={() => setStep("email")}
              sx={{
                fontSize: "13px",
                color: "primary.main",
                cursor: "pointer",
                fontWeight: 500,
              }}
            >
              {t("profile:personal_data.change_email_btn", {
                defaultValue: "Изменить e-mail",
              })}
            </Typography>
          </Box>
        </>
      )}
    </Drawer>
  );
}

function pickMessage(err: unknown): string {
  const raw =
    (err as { response?: { data?: { message?: string | string[] } } })?.response
      ?.data?.message;
  const msg = Array.isArray(raw) ? raw.join(", ") : raw;
  return msg || "Не удалось выполнить действие";
}
